import { handleError } from "helpers/sentry";
import { uiReadyTimeoutMs, waitForAtxUi } from "./atx-ui-ready";
import { AtxHandoffConfig, LegacyInitArgs, mapLegacyConfig, parseLegacyArgs } from "./legacy-config";
import { bridgeProctorClient } from "./proctor-client-bridge";
import { trace } from "./trace";

/**
 * Switcher: hands a proctoring session from this client (v7) to the ATX SDK
 * (v8), for orgs the backend has mapped to an ATX project.
 *
 * Everything the two clients need in common lives in this folder: loading the
 * bundle, translating the host's init config into ATX's, re-pointing
 * `window.ProctorClient3` at the ATX session, and deciding when ATX is ready
 * for this client to stand down. The ATX SDK itself is unmodified — it is
 * driven entirely through its public API.
 *
 * The switcher is deliberately self-contained and disposable: when hosts are
 * served the ATX bundle directly, this folder is what gets deleted.
 */

export { loadAtxBundle } from "./atx-sdk";
export { forcedAtxHandoff } from "./forced-handoff";
export { atxHandoffFor } from "./handoff-decision";
export type { AtxHandoffConfig } from "./legacy-config";

/**
 * ATX has a usable screen — this client can stop. The only teardown point:
 * until it fires, this client stays mounted and its splash, being a
 * full-screen overlay, is what covers ATX's boot.
 */
export const ATX_TEARDOWN_EVENT = "ProviewHandoffToAtx";

/**
 * Starts the ATX session, then stands this client down.
 *
 * The bundle is already loaded by this point. Anything that fails from here on
 * belongs to ATX — its session may already exist server-side, so there is no
 * falling back to this client's own flow.
 */
const runHandoff = async (atx: AtxHandoffConfig, legacyArgs: LegacyInitArgs): Promise<void> => {
  const sdk = window.Proview;
  if (!sdk?.session?.init) {
    throw new Error("ATX bundle is loaded but window.Proview.session.init is missing");
  }

  const { proctorToken, hostConfig } = parseLegacyArgs(legacyArgs);
  const config = mapLegacyConfig(legacyArgs, atx);

  // Neither the proctor token nor the attendee's details are logged: tracing
  // can be switched on in production from the console, so anything it prints
  // has to be safe for the candidate to read.
  trace("handing off to ATX", {
    hasProctorToken: !!proctorToken,
    legacyHostConfigKeys: Object.keys(hostConfig),
    hostCallbacks: {
      initCallback: typeof hostConfig.initCallback,
      errorCallback: typeof hostConfig.errorCallback,
      networkDisconnectionCallback: typeof hostConfig.networkDisconnectionCallback
    }
  });
  trace("mapped to ATX SessionInitConfig", {
    ...config,
    attendee: { identifier: config.attendee.identifier, fields: Object.keys(config.attendee) }
  });

  // Registered before init() so ATX config-validation errors are reported.
  await sdk.onError?.((error) => {
    trace("ATX reported an error", error);
    handleError(error, undefined, { atx_handoff: true });
  });

  bridgeProctorClient(sdk, hostConfig);

  const initialised = await sdk.session.init(config);
  trace("ATX session.init() resolved", { initialised });
  if (initialised === false) {
    // ATX reports some validation failures as a false result rather than a
    // rejection; starting on top of that would create a session it never
    // configured.
    throw new Error("ATX session.init() returned false — the session was not initialised");
  }

  // Not awaited: start() only settles once the candidate has finished
  // onboarding, long after ATX first renders.
  const started = sdk.session.start();
  // The wait below must not leave this rejection unobserved in the meantime.
  started.catch(() => undefined);

  if (!(await waitForAtxUi())) {
    // Terminal: this client stays on screen (an unresponsive ATX should look
    // like a session still loading, not like the host page with nothing on
    // it), and the host is never told monitoring began — which `started`
    // resolving below would otherwise do while the candidate is still sitting
    // on this client's splash.
    throw new Error(`ATX rendered no usable screen within ${uiReadyTimeoutMs}ms of the handoff`);
  }

  trace("ATX has a usable screen — tearing this client down");
  document.dispatchEvent(new CustomEvent(ATX_TEARDOWN_EVENT));

  const output = await started;
  trace("ATX session.start() resolved — the candidate is being monitored", output);

  // Same contract as this client's RecorderScreen: fired once, after
  // pre-checks pass and monitoring has actually begun. Never on failure.
  trace("invoking host initCallback(null, uuid, true, true, false)", { uuid: output.uuid });
  hostConfig.initCallback?.(null, output.uuid, true, true, false);
};

let inFlight: Promise<void> | null = null;

/**
 * One attempt gets one handoff at a time. A caller that fires twice — a
 * remount, React's StrictMode double-invoke, two effect runs — must not reach
 * `session.init()`/`start()` concurrently: that risks two ATX sessions for a
 * single attempt. The second call joins the first instead.
 */
export const handoffToAtx = (atx: AtxHandoffConfig, legacyArgs: LegacyInitArgs): Promise<void> => {
  if (inFlight) {
    trace("handoff already in flight — joining it rather than starting a second one");
    return inFlight;
  }

  const handoff = runHandoff(atx, legacyArgs);
  inFlight = handoff;
  const clear = () => {
    if (inFlight === handoff) inFlight = null;
  };
  handoff.then(clear, clear);

  return handoff;
};
